import React, { useState, useEffect } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const SMSLogs = () => {
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalDocs: 0 });
    const [filters, setFilters] = useState({
        seenFilter: '',
        startDate: '',
        endDate: '',
        trxID: '',
        last3: '',
        amount: ''
    });
    const [summary, setSummary] = useState({ found: 0, totalAmount: 0 });
    const [isSearched, setIsSearched] = useState(false);

    // ১. মেসেজ থেকে নিখুঁত টাকা বের করার ফাংশন
    const extractCashAmount = (msg) => {
        if (!msg) return 0;
        const normalizedMsg = msg.replace(/,/g, ''); // কমা সরিয়ে ফেলা
        // ট্রানজ্যাকশন অ্যামাউন্ট বের করার রেজেক্স
        let match = normalizedMsg.match(/(?:Cash In|Cash Out|received|Payment|Send|Amount:)\s*(?:Tk|BDT)?\s*([\d\.]+)/i) ||
                    normalizedMsg.match(/(?:Tk|BDT)\s*([\d\.]+)/i);
        
        return match ? parseFloat(match[1]) : 0;
    };

    // ২. মেসেজের ভেতর থেকে সব নম্বর বের করার ফাংশন (লাস্ট ৩ ডিজিট চেক করার জন্য)
    const extractNumbers = (msg) => {
        if (!msg) return [];
        let nums = msg.match(/[\d,\.]+/g) || [];
        return nums.map(n => n.replace(/,/g, "").trim());
    };

    useEffect(() => {
        socketService.connect();

        socketService.on('initial_sms_logs', (data) => {
            if (data && data.logs) {
                // সার্ভার সাইড থেকে ফিল্টার হয়ে আসলে এখানে সেট হবে
                setFilteredLogs(data.logs);
                setPagination({
                    currentPage: data.currentPage || 1,
                    totalPages: data.totalPages || 1,
                    totalDocs: data.totalDocs || 0
                });
                calculateSummary(data.logs);
            } else if (Array.isArray(data)) {
                setFilteredLogs(data);
                calculateSummary(data);
                setPagination({ currentPage: 1, totalPages: 1, totalDocs: data.length });
            }
        });

        socketService.on('new_sms', (newSms) => {
            setFilteredLogs((prev) => [newSms, ...prev]);
        });

        return () => socketService.disconnect();
    }, []);

    const calculateSummary = (logs) => {
        if (!Array.isArray(logs)) return;
        const total = logs.reduce((sum, log) => sum + extractCashAmount(log.message), 0);
        setSummary({ found: logs.length, totalAmount: total });
    };

    const fetchData = (page = 1) => {
        const { trxID, last3, amount } = filters;

        // ভ্যালিডেশন
        if (last3 && last3.length !== 3) return alert("⚠️ Last 3 Digits must be 3 digits!");
        if (trxID && trxID.length > 0 && trxID.length < 8) return alert("⚠️ TrxID must be at least 8 chars!");
        if (last3 && !amount) return alert("⚠️ Please provide Amount with Last 3 Digits!");

        // আমরা সার্ভারকে রিকোয়েস্ট পাঠাচ্ছি ফিল্টার করার জন্য
        socketService.emit('get-initial-sms', { ...filters, page });
        setIsSearched(true);
    };

    const handleSearch = () => fetchData(1);

    return (
        <Layout>
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; transition: 0.2s; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; font-weight: 600; height: 38px; cursor: pointer; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; margin-top: 20px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; text-align: left; }
                .custom-zebra-table td { padding: 12px 15px; border-bottom: 1px solid #edf2f7; font-size: 13px; }
                .pagination-wrapper { display: flex; justify-content: center; gap: 8px; margin-top: 25px; padding-bottom: 40px; }
                .page-nav-btn { background: #fff; border: 1px solid #cbd5e1; padding: 6px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }
                .page-nav-btn.active { background: #0284c7; color: #fff; border-color: #0284c7; }
                .page-nav-btn:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>

            <div className="main-content-area p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="section-title mb-0">SMS Log Management</h4>
                    {isSearched && (
                        <div className="count-badge">
                            Found: <span>{summary.found}</span> | Page Amount: <span>Tk {summary.totalAmount.toLocaleString()}</span>
                        </div>
                    )}
                </div>

                {/* Filter UI */}
                <div className="filter-card shadow-sm mb-4">
                    <div className="row g-3">
                        <div className="col-md-2">
                            <div className="field-label">Seen Filter</div>
                            <input type="text" className="modern-input" placeholder="Name..." onChange={(e) => setFilters({...filters, seenFilter: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <div className="field-label">Start Date</div>
                            <input type="date" className="modern-input" onChange={(e) => setFilters({...filters, startDate: e.target.value})} />
                        </div>
                        <div className="col-md-2">
                            <div className="field-label">TrxID</div>
                            <input type="text" className="modern-input" placeholder="8+ chars" onChange={(e) => setFilters({...filters, trxID: e.target.value})} />
                        </div>
                        <div className="col-md-4">
                            <div className="field-label">Last 3 Digits + Amount</div>
                            <div className="d-flex gap-2">
                                <input type="text" className="modern-input" placeholder="689" maxLength="3" onChange={(e) => setFilters({...filters, last3: e.target.value})} />
                                <input type="text" className="modern-input" placeholder="2000" onChange={(e) => setFilters({...filters, amount: e.target.value})} />
                            </div>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn-apply w-100" onClick={handleSearch}>Apply Search</button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="table-container shadow-sm">
                    <table className="custom-zebra-table">
                        <thead>
                            <tr>
                                <th>Sim / Device</th>
                                <th>Sender</th>
                                <th>Message</th>
                                <th>Timestamp</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLogs.length > 0 ? (
                                filteredLogs.map((log, index) => (
                                    <tr key={log._id || index}>
                                        <td>
                                            <div className="fw-bold">{log.simno}</div>
                                            <div style={{fontSize:'10px', color:'#64748b'}}>{log.deviceName}</div>
                                        </td>
                                        <td style={{color:'#6366f1', fontWeight:'800'}}>{log.senderID}</td>
                                        <td style={{maxWidth:'450px', whiteSpace:'pre-line'}}>{log.message}</td>
                                        <td>
                                            <div className="fw-bold">{moment(log.receivedAt).format('DD MMM, YYYY')}</div>
                                            <div style={{fontSize:'11px'}}>{moment(log.receivedAt).format('hh:mm A')}</div>
                                        </td>
                                        <td>
                                            {log.seenBy ? (
                                                <span className="badge bg-success" style={{fontSize:'10px'}}>Seen by {log.seenBy}</span>
                                            ) : (
                                                <span className="badge bg-secondary" style={{fontSize:'10px'}}>Unseen</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-5 text-muted">No records found. Try search.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="pagination-wrapper">
                        <button className="page-nav-btn" disabled={pagination.currentPage === 1} onClick={() => fetchData(pagination.currentPage - 1)}>Prev</button>
                        {[...Array(pagination.totalPages)].map((_, i) => {
                            const pNum = i + 1;
                            if (pNum === 1 || pNum === pagination.totalPages || Math.abs(pNum - pagination.currentPage) <= 2) {
                                return <button key={pNum} className={`page-nav-btn ${pagination.currentPage === pNum ? 'active' : ''}`} onClick={() => fetchData(pNum)}>{pNum}</button>;
                            } else if (Math.abs(pNum - pagination.currentPage) === 3) {
                                return <span key={pNum}>...</span>;
                            }
                            return null;
                        })}
                        <button className="page-nav-btn" disabled={pagination.currentPage === pagination.totalPages} onClick={() => fetchData(pagination.currentPage + 1)}>Next</button>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SMSLogs;